import type { QualityTable } from "./quality";

export interface CatalogueCharacter {
  name: string;
  sheet: string;
  baked: string;
  fingerprint: string;
}

/** What the harvested rows do not carry, keyed by source file and then by row id. */
export interface CuratedRow {
  shops?: string[];
  /** The count the library states where that is not one, which every row is baked at. */
  quantity?: number;
}

/** One line of the missing-kit table: a skill, and the items any one of which serves it. */
export interface KitEntry {
  skill: string;
  items: { source: string; id: string }[];
}

export interface Catalogue {
  gcs: string;
  library: string;
  characters: Record<string, CatalogueCharacter>;
  /** Every shop the curation declares, in the order it declares them. */
  shops?: string[];
  kit?: KitEntry[];
  /** The campaign rule the spell page control prints beside its select. */
  spell_page_advisory?: string;
  sidecar?: Record<string, Record<string, CuratedRow>>;
  quality: QualityTable;
}

/** How many of a row one purchase is, for the rows where that is not one. */
export function countsByItem(catalogue: Catalogue): Map<string, number> {
  const counts = new Map<string, number>();
  for (const rows of Object.values(catalogue.sidecar ?? {})) {
    for (const [id, curated] of Object.entries(rows)) {
      if (curated.quantity !== undefined) counts.set(id, curated.quantity);
    }
  }
  return counts;
}

export function shopsByItem(catalogue: Catalogue): Map<string, string[]> {
  const stocked = new Map<string, string[]>();
  for (const rows of Object.values(catalogue.sidecar ?? {})) {
    for (const [id, curated] of Object.entries(rows)) {
      if (curated.shops !== undefined && curated.shops.length > 0) stocked.set(id, curated.shops);
    }
  }
  return stocked;
}

/** A harvested GCS equipment row, carried verbatim into the splice. */
export interface BakedRow {
  id: string;
  /** The name the row is bought under, which a curated `name` may have replaced. */
  description: string;
  /** The library description, written only where a curated `name` differs from it. */
  stock_description?: string;
  calc: { value?: number; weight?: string };
  /** What a discounting shop sells the row at, written only on the rows such a shop stocks. */
  discount_value?: number;
  [field: string]: unknown;
}

export async function loadCatalogue(): Promise<Catalogue> {
  const response = await fetch("/catalogue.json");
  if (!response.ok) throw new Error(`catalogue: ${response.status}`);
  return (await response.json()) as Catalogue;
}

export async function loadSheet(entry: CatalogueCharacter): Promise<string> {
  const response = await fetch(`/${entry.sheet}`);
  if (!response.ok) throw new Error(`sheet ${entry.sheet}: ${response.status}`);
  return await response.text();
}

/** The rows this character may buy, and what buying them moves. */
export interface Baked {
  rows: BakedRow[];
  /** Passed to the panel verbatim; only the wasm side reads inside it. */
  deltas: string;
}

export async function loadBaked(entry: CatalogueCharacter): Promise<Baked> {
  const response = await fetch(`/${entry.baked}`);
  if (!response.ok) throw new Error(`baked ${entry.baked}: ${response.status}`);
  const baked = (await response.json()) as { rows: BakedRow[]; deltas?: unknown };
  return { rows: baked.rows, deltas: JSON.stringify(baked.deltas ?? {}) };
}
